import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import enginesService from '../../services/enginesService';
import { useFacility } from '../../context/FacilityContext';

/**
 * One connection card for every connected service.
 *
 * The marketing and camera engines each have a hand-written card. This one
 * is driven by the service's registry entry, so a new engine gets its card
 * for free. Rules that hold for all of them:
 *
 * - A stored credential is never shown again — the field stays blank and
 *   blank means "keep it".
 * - "Your key was rejected" and "the engine is unreachable" are DIFFERENT
 *   messages, because they need different fixes.
 * - Values come from the operator's provider; this product names no vendor.
 */
const Pill = ({ ok, children }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
      ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
    }`}
  >
    {ok ? <CheckCircle2 className="h-3 w-3" /> : null}
    {children}
  </span>
);

const EngineConnectionCard = ({ service, label, description }) => {
  const { facilityId } = useFacility();
  const [status, setStatus] = useState(null);
  const [engineUrl, setEngineUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    try {
      const res = await enginesService.connection(service, facilityId);
      setStatus(res.data);
      setEngineUrl(res.data?.engine_url || '');
    } catch {
      setStatus(null);
    }
  }, [facilityId, service]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await enginesService.connect(service, facilityId, {
        engineUrl: engineUrl.trim(),
        apiKey: apiKey.trim(),
        webhookSecret: secret.trim(),
      });
      setResult(res.data);
      if (res.data?.connected) {
        toast.success(
          apiKey.trim() ? `Connected to ${label}.` : 'Connection verified.',
        );
        setApiKey('');
        setSecret('');
        await load();
      }
    } catch (err) {
      const detail = err?.response?.data?.error;
      setResult({ connected: false, error: detail?.code || 'engine_error',
        message: detail?.message });
      toast.error(detail?.message || 'Could not verify the connection.');
    } finally {
      setBusy(false);
    }
  };

  const connected = !!status?.connected;
  const buttonLabel = busy
    ? 'Verifying…'
    : apiKey.trim()
      ? (connected ? 'Replace key and verify' : 'Connect and verify')
      : 'Verify connection';

  return (
    <div
      data-testid={`engine-card-${service}`}
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-slate-900 capitalize">
            {label}
          </h3>
          <p className="mt-0.5 text-[12px] text-slate-500">{description}</p>
        </div>
        <Pill ok={connected}>{connected ? 'Connected' : 'Not connected'}</Pill>
      </div>

      {connected && status?.tenant_name ? (
        <p className="mt-3 text-[12px] text-slate-600">
          Connected as <span className="font-medium">{status.tenant_name}</span>
          {status.connected_at ? ` · since ${status.connected_at.slice(0, 10)}` : ''}
        </p>
      ) : null}

      {result && result.connected === false && result.error === 'key_rejected' ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 p-2.5 text-[12px] text-rose-700">
          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            The provider rejected that key. Ask your provider for a current key
            for this facility.
          </span>
        </div>
      ) : null}

      {result && result.connected === false && result.error === 'unreachable' ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-2.5 text-[12px] text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Nothing answered at that address. Nothing was saved — check the
            address and try again.
          </span>
        </div>
      ) : null}

      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="text-[12px] font-medium text-slate-700">Address</span>
          <input
            data-testid={`engine-url-${service}`}
            type="url"
            name={`engine-url-${service}`}
            value={engineUrl}
            onChange={(e) => setEngineUrl(e.target.value)}
            placeholder={status?.default_engine_url || 'https://…'}
            // A URL beside two password inputs reads to the browser as a
            // login form: it filled the operator's email address here and a
            // saved password into Key. These fields are never a sign-in.
            autoComplete="off"
            spellCheck={false}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px]"
          />
          <span className="mt-1 block text-[11px] text-slate-500">
            The address your provider gave you. Stored per facility —
            replace it to use a different one here.
          </span>
        </label>

        <label className="block">
          <span className="text-[12px] font-medium text-slate-700">Key</span>
          <input
            type="password"
            name={`engine-key-${service}`}
            autoComplete="new-password"
            spellCheck={false}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={
              status?.has_api_key
                ? '•••• stored — leave blank to keep'
                : 'Paste the key your provider issued for this facility'
            }
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px]"
          />
          <span className="mt-1 block text-[11px] text-slate-500">
            The stored key is encrypted and never shown again. Type a new one
            only to replace it.
          </span>
        </label>

        <label className="block">
          <span className="text-[12px] font-medium text-slate-700">
            Webhook secret
          </span>
          <input
            type="password"
            name={`engine-secret-${service}`}
            autoComplete="new-password"
            spellCheck={false}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={
              status?.has_webhook_secret
                ? '•••• stored — leave blank to keep'
                : 'From your provider, for this facility'
            }
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px]"
          />
          <span className="mt-1 block text-[11px] text-slate-500">
            Lets this product verify what the provider sends for this facility,
            so nothing else can pose as it. One per facility.
          </span>
        </label>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={busy || !facilityId}
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-[13px] font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {buttonLabel}
        </button>
      </div>

      {result && result.checks ? (
        <div className="mt-3 space-y-0.5 border-t border-slate-100 pt-2.5 text-[12px] text-slate-600">
          <p>{result.checks.reachable ? '✓' : '✗'} Provider reachable</p>
          <p>{result.checks.key_accepted ? '✓' : '✗'} Key accepted</p>
        </div>
      ) : null}
    </div>
  );
};

export default EngineConnectionCard;
